import {
  formatDateToLocale,
  formatDateToUtcIsoString,
  formatPercentage,
  Formats,
  getCurrencySymbol,
} from '@toggle/helpers';
import { EarningsItemBase, Entity, mapEntity } from '@toggle/toggle';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { useOneDayReturnLivePrice } from '~/hooks/use-one-day-return-live-price/useOneDayReturnLivePrice';
import { useFetchSnakeByName } from '~/react-query-hooks/snakes/useFetchSnakeByName';
import { useLanguageStore } from '~/stores/use-language-store/useLanguageStore';
import {
  getOneDayReactionDate,
  getOneDayReactionValue,
  getReportingTimeIcon,
} from '~/widgets/earnings/utils/utils';

import * as S from './EarningsInsightSection.styles';
import { formatCurrency, getVariant } from './utils';

const ONE_DAY_RETURN_SNAKE = 'mom(horiz=1d)';

export interface RecapContentProps {
  earningsData: EarningsItemBase;
  entity: Entity;
}

export const RecapContent = ({ earningsData, entity }: RecapContentProps) => {
  const { t } = useTranslation(['widget']);
  const languageSetting = useLanguageStore(state => state.languageSetting);
  const currencySymbol = getCurrencySymbol(earningsData.currency);

  const mappedEntity = useMemo(() => mapEntity(entity), [entity]);
  const { oneDayLiveReturn } = useOneDayReturnLivePrice(mappedEntity);

  const reportingDate = earningsData.reporting_date
    ? new Date(earningsData.reporting_date)
    : undefined;

  const formattedDate = reportingDate
    ? formatDateToLocale(languageSetting, reportingDate, Formats['PP'])
    : '-';

  const dateToUse = useMemo(
    () => getOneDayReactionDate(reportingDate),
    [reportingDate]
  );

  const formattedFromDate = dateToUse
    ? formatDateToUtcIsoString(dateToUse)
    : undefined;

  const { data: snakeData } = useFetchSnakeByName(
    `${entity.tag}.${ONE_DAY_RETURN_SNAKE}`,
    undefined,
    formattedFromDate,
    !formattedFromDate
  );

  const oneDayReaction = useMemo(
    () =>
      getOneDayReactionValue(
        dateToUse,
        oneDayLiveReturn,
        snakeData,
        formattedFromDate
      ),
    [dateToUse, oneDayLiveReturn, snakeData, formattedFromDate]
  );

  const epsValue = earningsData.eps?.value;
  const epsSurprise = earningsData.eps?.surprise;

  const timeIcon = reportingDate
    ? getReportingTimeIcon(reportingDate)
    : undefined;

  return (
    <>
      <S.MetricContainer>
        <S.MetricLabel>
          {t('widget:assetOverview.earningsSection.reportedOn')}
        </S.MetricLabel>
        <S.DateContainer>
          <S.MetricValue>{formattedDate}</S.MetricValue>
          {timeIcon && <S.TimeIcon iconName={timeIcon} size={16} />}
        </S.DateContainer>
      </S.MetricContainer>
      <S.MetricContainer>
        <S.MetricLabel>
          {t('widget:assetOverview.earningsSection.epsActual')}
        </S.MetricLabel>
        <S.MetricValueMono>
          {formatCurrency({
            value: epsValue,
            currencySymbol,
            showPositiveSign: false,
          })}
        </S.MetricValueMono>
      </S.MetricContainer>
      <S.MetricContainer>
        <S.MetricLabel>
          {t('widget:assetOverview.earningsSection.epsSurprise')}
        </S.MetricLabel>
        <S.MetricValueMono $variant={getVariant(epsSurprise)}>
          {formatCurrency({
            value: epsSurprise,
            currencySymbol,
            showPositiveSign: true,
          })}
        </S.MetricValueMono>
      </S.MetricContainer>
      <S.MetricContainer>
        <S.MetricLabel>
          {t('widget:assetOverview.earningsSection.oneDayReaction')}
        </S.MetricLabel>
        <S.MetricValueMono $variant={getVariant(oneDayReaction)}>
          {oneDayReaction !== undefined
            ? formatPercentage(oneDayReaction, {
                suffix: '%',
                addPlus: true,
                useOriginalValue: true,
              })
            : '-'}
        </S.MetricValueMono>
      </S.MetricContainer>
    </>
  );
};
